import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Avatar,
  Button,
  Typography,
  Row,
  Col,
  Descriptions,
  Tag,
  Divider,
  Space,
  ConfigProvider,
  Spin,
  message,
  Upload
} from 'antd';
import {
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  EditOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  BankOutlined,
  LoadingOutlined,
  CameraOutlined,
  InstagramOutlined,
  FacebookOutlined,
  LinkedinOutlined,
  TwitterOutlined
} from '@ant-design/icons';
import companyApi from '../../api/companyApi';
import EditProfile from './EditProfile';

const { Title, Text, Link } = Typography;
const { Content } = Layout;

const Profile = () => {
  const navigate = useNavigate();
  const brandColor = '#3C007A';
  const accentColor = '#FF8F00';
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);

  // Default fallback images
  const defaultLogo = 'https://ui-avatars.com/api/?name=Company&background=3C007A&color=fff&size=128&bold=true';
  const defaultBanner = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop';

  // Helper function to get image URL from server
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    const apiUrl = process.env.REACT_APP_API_URL ;
    
    // If imagePath is already a full URL, return it
    if (imagePath.startsWith('http')) return imagePath;
    
    // Convert Windows backslashes to forward slashes
    let normalizedPath = imagePath.replace(/\\/g, '/');
    // Strip any leading slash so we can consistently prefix below
    normalizedPath = normalizedPath.replace(/^\/+/, '');

    // If path already includes the uploads/ segment, use it directly
    if (normalizedPath.startsWith('uploads/')) {
      return `${apiUrl}/${normalizedPath}`;
    }

    // Otherwise, assume it's just the filename
    return `${apiUrl}/uploads/${normalizedPath}`;
  };

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);

      // Get user info from localStorage
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      // console.log('User Info:', userInfo);

      // Fetch company profile using the company field from userInfo
      // userInfo.company contains the company _id: 693bb71cb2c43a05f5c610d8
      if (userInfo.company) {
        const data = await companyApi.getCompanyProfile(userInfo.company);
        // console.log('Company Data:', data);
        setCompanyData(data);
        // message.success('Profile loaded successfully');
      } else {
        message.error('No company ID found. Please login again.');
      }
    } catch (error) {
      message.error('Failed to load company profile');
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (file) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const formData = new FormData();
      formData.append('logo', file);

      // Use company ID for update
      await companyApi.updateCompanyProfile(userInfo.company, formData);
      message.success('Logo uploaded successfully');
      fetchCompanyProfile(); // Refresh profile data
      return false; // Prevent auto upload
    } catch (error) {
      message.error('Failed to upload logo');
      return false;
    }
  };

  const handleBannerUpload = async (file) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const formData = new FormData();
      formData.append('banner', file);

      // Use company ID for update
      await companyApi.updateCompanyProfile(userInfo.company, formData);
      message.success('Banner uploaded successfully');
      fetchCompanyProfile(); // Refresh profile data
      return false; // Prevent auto upload
    } catch (error) {
      message.error('Failed to upload banner');
      return false;
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'verified': 'success',
      'pending': 'warning',
      'active': 'success',
      'approved': 'success',
      'inactive': 'default',
      'rejected': 'error'
    };
    return statusMap[status?.toLowerCase()] || 'default';
  };

  const getStatusText = (status) => {
    const statusTextMap = {
      'approved': 'Verified',
      'pending': 'Pending',
      'rejected': 'Rejected',
      'active': 'Active',
      'verified': 'Verified',
      'inactive': 'Inactive'
    };
    return statusTextMap[status?.toLowerCase()] || status || 'Pending';
  };

  const handleEditProfile = () => {
    setEditModalVisible(true);
  };

  const handleEditSuccess = () => {
    fetchCompanyProfile(); // Refresh profile data after successful edit
  };

  // Get logo and banner URLs with error state
  const [bannerError, setBannerError] = useState(false);
  const logoUrl = companyData?.logo ? getImageUrl(companyData.logo) : defaultLogo;
  const bannerUrl = companyData?.banner ? getImageUrl(companyData.banner) : defaultBanner;

  // console.log('Logo URL:', logoUrl);
  // console.log('Banner URL:', bannerUrl);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f0f2f5' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: brandColor }} spin />} />
      </div>
    );
  }

  if (!companyData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', backgroundColor: '#f0f2f5' }}>
        <Text type="secondary">No company data available. Please contact support.</Text>
      </div>
    );
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: brandColor,
          fontFamily: "'Rubik', sans-serif",
          borderRadius: 6,
          fontSize: 16,
        },
      }}
    >
      <div style={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#f0f2f5',
        position: 'relative',
        overflowX: 'hidden',
        fontFamily: "'Rubik', sans-serif"
      }}>
        {/* Banner Section */}
        <div style={{
          height: 'clamp(200px, 30vh, 320px)',
          background: `url(${bannerError ? defaultBanner : bannerUrl}) center center / cover no-repeat`,
          position: 'relative',
          width: '100%',
          maxWidth: '100%'
        }}>
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(to bottom, rgba(60, 0, 122, 0.3), rgba(0,0,0,0.6))'
          }} />

          {/* Hidden image to check if banner loads */}
          <img 
            src={bannerUrl} 
            style={{ display: 'none' }}
            onError={() => {
              console.error('Banner failed to load:', bannerUrl);
              setBannerError(true);
            }}
            onLoad={() => console.log('Banner loaded successfully')}
            alt="banner"
          />

          {/* Upload Banner Button */}
          <Upload
            showUploadList={false}
            beforeUpload={handleBannerUpload}
            accept="image/*"
          >
            <Button
              icon={<CameraOutlined />}
              style={{ position: 'absolute', top: 20, right: 20, zIndex: 2 }}
            >
              Change Banner
            </Button>
          </Upload>
        </div>

        {/* Content Container */}
        <div style={{
          padding: '0 clamp(2px, 3vw, 48px) 24px',
          width: '100%',
          maxWidth: '1600px',
          margin: '0 auto',
          boxSizing: 'border-box'
        }}>
          {/* Header Card */}
          <Card
            bordered={false}
            style={{
              marginTop: 'clamp(-80px, -8vh, -60px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              position: 'relative',
              zIndex: 1,
              width: '100%',
              maxWidth: '100%',
              borderRadius: '12px'
            }}
            bodyStyle={{ padding: 'clamp(20px, 3vw, 32px)' }}
          >
            <Row gutter={[24, 24]} align="middle" wrap>
              <Col xs={24} sm={24} md={6} lg={4} xl={3} style={{ textAlign: 'center', position: 'relative' }}>
                <div style={{ 
                  width: 'clamp(100px, 12vw, 140px)', 
                  height: 'clamp(100px, 12vw, 140px)', 
                  borderRadius: '50%',
                  marginTop: 'clamp(-40px, -4vh, -30px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  position: 'relative',
                  margin: 'clamp(-40px, -4vh, -30px) auto 0'
                }}>
                  {logoError || !companyData?.logo ? (
                    <Avatar
                      style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: brandColor,
                        fontSize: 'clamp(32px, 4vw, 48px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                      }}
                      icon={<UserOutlined />}
                    >
                      {(companyData?.companyName || companyData?.name)?.charAt(0)?.toUpperCase()}
                    </Avatar>
                  ) : (
                    <img
                      src={logoUrl}
                      alt="Company Logo"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        padding: '8px'
                      }}
                      onError={() => {
                        console.error('Logo failed to load:', logoUrl);
                        setLogoError(true);
                      }}
                      onLoad={() => console.log('Logo loaded successfully')}
                    />
                  )}
                </div>
                <Upload
                  showUploadList={false}
                  beforeUpload={handleLogoUpload}
                  accept="image/*"
                >
                  <Button
                    icon={<CameraOutlined />}
                    size="small"
                    shape="circle"
                    style={{ position: 'absolute', bottom: -20, left: '50%', transform: 'translateX(-50%)' }}
                  />
                </Upload>
              </Col>

              <Col xs={24} sm={24} md={12} lg={13} xl={14}>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Title
                    level={2}
                    style={{
                      margin: 0,
                      color: brandColor,
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 500,
                      fontSize: 'clamp(22px, 3vw, 32px)'
                    }}
                  >
                    {companyData.companyName || companyData.name || 'Company Name'}
                  </Title>
                  <Space wrap size={[8, 8]}>
                    <Tag
                      color="purple"
                      style={{
                        margin: 0,
                        fontSize: 'clamp(13px, 1.2vw, 16px)',
                        padding: '4px 12px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400
                      }}
                    >
                      {companyData.industry || companyData.industryType || companyData.sector || 'Not Specified'}
                    </Tag>
                    <Tag
                      icon={<SafetyCertificateOutlined />}
                      color={getStatusColor(companyData.status || companyData.accountStatus)}
                      style={{
                        fontSize: 'clamp(13px, 1.2vw, 16px)',
                        padding: '4px 12px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400
                      }}
                    >
                      {getStatusText(companyData.status || companyData.accountStatus)}
                    </Tag>
                  </Space>
                  {companyData.website && (
                    <Link
                      href={companyData.website}
                      target="_blank"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 'clamp(14px, 1.2vw, 16px)',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400,
                        color: "#333",
                        wordBreak: 'break-all'
                      }}
                    >
                      <GlobalOutlined /> {companyData.website}
                    </Link>
                  )}

                  {/* Social Networks */}
                  {(companyData.socialNetworks?.instagram || companyData.socialNetworks?.facebook ||
                    companyData.socialNetworks?.linkedin || companyData.socialNetworks?.twitter) && (
                      <Space size={[16, 8]} wrap style={{ marginTop: 8 }}>
                        {companyData.socialNetworks?.instagram && (
                          <Link href={companyData.socialNetworks.instagram} target="_blank">
                            <InstagramOutlined style={{ fontSize: 'clamp(20px, 2vw, 24px)', color: '#E4405F' }} />
                          </Link>
                        )}
                        {companyData.socialNetworks?.facebook && (
                          <Link href={companyData.socialNetworks.facebook} target="_blank">
                            <FacebookOutlined style={{ fontSize: 'clamp(20px, 2vw, 24px)', color: '#1877F2' }} />
                          </Link>
                        )}
                        {companyData.socialNetworks?.linkedin && (
                          <Link href={companyData.socialNetworks.linkedin} target="_blank">
                            <LinkedinOutlined style={{ fontSize: 'clamp(20px, 2vw, 24px)', color: '#0A66C2' }} />
                          </Link>
                        )}
                        {companyData.socialNetworks?.twitter && (
                          <Link href={companyData.socialNetworks.twitter} target="_blank">
                            <TwitterOutlined style={{ fontSize: 'clamp(20px, 2vw, 24px)', color: '#1DA1F2' }} />
                          </Link>
                        )}
                      </Space>
                    )}
                </Space>
              </Col>

              <Col xs={24} sm={24} md={6} lg={7} xl={7} style={{ 
                textAlign: 'center',
                display: 'flex', 
                justifyContent: { xs: 'center', md: 'flex-end' },
                gap: '12px',
                flexWrap: 'wrap',
                marginTop: { xs: 16, md: 0 }
              }}>
                <Button
                  icon={<MailOutlined />}
                  onClick={() => navigate('/dashboard/contact')}
                  style={{
                    fontSize: 'clamp(14px, 1.2vw, 16px)',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    height: 'clamp(36px, 4vh, 40px)',
                    flex: '1 1 auto',
                    minWidth: '120px'
                  }}
                >
                  Message
                </Button>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={handleEditProfile}
                  style={{
                    fontSize: 'clamp(14px, 1.2vw, 16px)',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    height: 'clamp(36px, 4vh, 40px)',
                    backgroundColor: brandColor,
                    flex: '1 1 auto',
                    minWidth: '120px'
                  }}
                >
                  Edit Profile
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Details Grid */}
          <Row gutter={[{ xs: 4, sm: 16, md: 24 }, { xs: 12, sm: 24 }]} style={{ marginTop: 24, width: '100%' }}>

            {/* Left Column: Organization Details */}
            <Col xs={24} sm={24} md={24} lg={16} xl={16}>
              <Card
                title={
                  <Space style={{ fontSize: 'clamp(16px, 2vw, 20px)', fontFamily: "'Rubik', sans-serif", fontWeight: 400, color: brandColor }}>
                    <BankOutlined style={{ color: brandColor, fontSize: 'clamp(16px, 2vw, 20px)' }} />
                    Organization Details
                  </Space>
                }
                bordered={false}
                style={{ height: '100%', borderRadius: '12px' }}
                bodyStyle={{ padding: 'clamp(16px, 2vw, 24px)' }}
              >
                <Descriptions
                  column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}
                  layout="vertical"
                  labelStyle={{
                    fontWeight: 400,
                    color: '#666',
                    fontSize: 'clamp(14px, 1.2vw, 16px)',
                    fontFamily: "'Rubik', sans-serif"
                  }}
                  contentStyle={{
                    fontSize: 'clamp(15px, 1.3vw, 17px)',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: brandColor
                  }}
                >
                  <Descriptions.Item label="Company Name">
                    <Text strong style={{ fontSize: 'clamp(16px, 1.4vw, 18px)', fontFamily: "'Rubik', sans-serif", fontWeight: 400, color: brandColor }}>
                      {companyData.companyName || companyData.name || 'N/A'}
                    </Text>
                  </Descriptions.Item>

                  <Descriptions.Item label="Director / CEO Name">
                    <Space>
                      <Text strong style={{ fontSize: 'clamp(15px, 1.3vw, 17px)', fontFamily: "'Rubik', sans-serif", fontWeight: 400 }}>
                        {companyData.directorCeo || 'N/A'}
                      </Text>
                    </Space>
                  </Descriptions.Item>

                  <Descriptions.Item label="Industry Sector">
                    {companyData.industry || companyData.industryType || companyData.sector || 'Not Specified'}
                  </Descriptions.Item>

                  <Descriptions.Item label="Registration ID">
                    <Text
                      copyable
                      style={{
                        fontSize: 15,
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400,
                        color: "#333",
                        letterSpacing: '0.5px'
                      }}
                    >
                      {companyData.registrationId || `#AID${companyData._id?.slice(-4).toUpperCase()}` || 'N/A'}
                    </Text>
                  </Descriptions.Item>

                  {companyData.gstin && (
                    <Descriptions.Item label="GSTIN">
                      <Text copyable style={{ fontSize: 'clamp(14px, 1.2vw, 15px)' }}>{companyData.gstin}</Text>
                    </Descriptions.Item>
                  )}

                  {companyData.yearEstablished && (
                    <Descriptions.Item label="Year Established">
                      {companyData.yearEstablished}
                    </Descriptions.Item>
                  )}

                  {companyData.numberOfEmployees && (
                    <Descriptions.Item label="Number of Employees">
                      {companyData.numberOfEmployees}
                    </Descriptions.Item>
                  )}

                  {companyData.annualTurnover && (
                    <Descriptions.Item label="Annual Turnover">
                      ₹ {companyData.annualTurnover}
                    </Descriptions.Item>
                  )}
                </Descriptions>

                <Divider />

                <Title
                  level={5}
                  style={{
                    marginBottom: 20,
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    fontSize: 'clamp(16px, 2vw, 20px)',
                    color: brandColor
                  }}
                >
                  About
                </Title>
                <Text
                  type="secondary"
                  style={{
                    lineHeight: 1.8,
                    fontSize: 'clamp(14px, 1.2vw, 16px)',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: '#333',
                    display: 'block'
                  }}
                >
                  {companyData.description || companyData.aboutCompany || companyData.about ||
                    `${companyData.companyName || companyData.name || 'Our company'} is a leading player in the ${companyData.industry || companyData.industryType || 'business'} sector. 
                  We are committed to delivering excellence and innovation.`}
                </Text>
              </Card>
            </Col>

            {/* Right Column: Contact Information */}
            <Col xs={24} sm={24} md={24} lg={8} xl={8}>
              <Card
                title={
                  <Space style={{
                    fontSize: 'clamp(16px, 2vw, 20px)',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: '#3C007A'
                  }}>
                    <EnvironmentOutlined style={{ color: brandColor, fontSize: 'clamp(16px, 2vw, 20px)' }} />
                    Contact Information
                  </Space>
                }
                bordered={false}
                style={{
                  height: '100%',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
                headStyle={{
                  borderBottom: 'none',
                  padding: 'clamp(12px, 2vw, 16px)'
                }}
                bodyStyle={{
                  padding: 'clamp(16px, 2vw, 24px)'
                }}
              >

                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                  <Divider style={{ margin: '3px 0' }} />
                  {/* Contact Person */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 'clamp(40px, 5vw, 48px)',
                      height: 'clamp(40px, 5vw, 48px)',
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <UserOutlined style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: brandColor }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text type="secondary" style={{ fontSize: 'clamp(14px, 1.2vw, 16px)', display: 'block', marginBottom: 4, fontWeight: 400, fontFamily: "'Rubik', sans-serif", color: '#666' }}>
                        Contact Person</Text>
                      <Text style={{ fontSize: 'clamp(15px, 1.3vw, 17px)', color: '#333', fontFamily: "'Rubik', sans-serif", fontWeight: 500, wordBreak: 'break-word' }}>
                        {companyData.contactName || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  {/* Email */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 'clamp(40px, 5vw, 48px)',
                      height: 'clamp(40px, 5vw, 48px)',
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <MailOutlined style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: brandColor }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text type="secondary" style={{ fontSize: 'clamp(14px, 1.2vw, 16px)', display: 'block', marginBottom: 4, fontWeight: 400, fontFamily: "'Rubik', sans-serif", color: '#666' }}>Email Address</Text>
                      <Link 
                        href={`mailto:${companyData.email || companyData.contactEmail || ''}`}
                        style={{ fontSize: 'clamp(15px, 1.3vw, 17px)', color: brandColor, fontFamily: "'Rubik', sans-serif", fontWeight: 500, wordBreak: 'break-word', textDecoration: 'none' }}
                      >
                        {companyData.email || companyData.contactEmail || 'N/A'}
                      </Link>
                    </div>
                  </div>

                  {/* Phone */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 'clamp(40px, 5vw, 48px)',
                      height: 'clamp(40px, 5vw, 48px)',
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <PhoneOutlined style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: brandColor }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text type="secondary" style={{ fontSize: 'clamp(14px, 1.2vw, 16px)', display: 'block', marginBottom: 4, fontWeight: 400, fontFamily: "'Rubik', sans-serif", color: '#666' }}>Mobile Number</Text>
                      <Link 
                        href={`tel:${companyData.phone || companyData.contactNumber || companyData.mobileNumber || ''}`}
                        style={{ fontSize: 'clamp(15px, 1.3vw, 17px)', color: brandColor, fontFamily: "'Rubik', sans-serif", fontWeight: 500, textDecoration: 'none' }}
                      >
                        {companyData.phone || companyData.contactNumber || companyData.mobileNumber || 'N/A'}
                      </Link>
                    </div>
                  </div>

                  <Divider style={{ margin: '0px 0' }} />

                  {/* Registered Address - Centered Layout */}
                  <div style={{ textAlign: 'center', marginTop: 0 }}>
                    <Text type="secondary" style={{ fontSize: 'clamp(16px, 1.5vw, 18px)', marginBottom: 8, display: 'block', fontWeight: 500 }}>
                      Registered Address
                    </Text>

                    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <EnvironmentOutlined style={{ color: brandColor, marginTop: 4, fontSize: 'clamp(14px, 1.2vw, 16px)' }} />
                      <Text style={{
                        fontSize: 'clamp(13px, 1.1vw, 14px)',
                        color: '#333',
                        maxWidth: '90%',
                        lineHeight: 1.6,
                        textAlign: 'center'
                      }}>
                        {companyData.address?.street ? `${companyData.address.street}, ` : ''}
                        {companyData.address?.city}
                        {(companyData.address?.state) ? `, ${companyData.address.state}` : ''}
                        <br />
                        {companyData.address?.country || 'India'} - {companyData.address?.pincode || ''}
                      </Text>
                    </div>
                  </div>

                </Space>
              </Card>
            </Col>

          </Row>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <EditProfile
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        onSuccess={handleEditSuccess}
      />
    </ConfigProvider>
  );
};

export default Profile;